import { useContext, useEffect, useLayoutEffect, useState, type CSSProperties, type MouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { Box, Button, Paper, Typography } from '@mui/material'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import type { TutorialPlacement, TutorialStep } from './tutorial-steps'
import { useDiRef } from '@/common/react/di-hooks.ts'
import type { TutorialService } from '@/tutorial/TutorialService.ts'

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

const SPOTLIGHT_PAD = 6
const POP_WIDTH = 360
const POP_OFFSET = 14
const Z_BACKDROP = 2000
const Z_BORDER = 2001
const Z_BLOCK = 2002
const Z_POP = 2003

export function TutorialOverlay() {
  const service = useDiRef<TutorialService>('TutorialService')
  const currentStepId = useBehaviorObservable(service?.currentStepId$)
  const targets = useBehaviorObservable(service?.targets$)
  const [rect, setRect] = useState<Rect | null>(null)

  const step = currentStepId ? service?.getStep(currentStepId) : undefined
  const stepPos = currentStepId ? service?.getStepPosition(currentStepId) : undefined
  const targetEl = step ? targets?.get(step.targetId) : undefined

  useLayoutEffect(() => {
    setRect(null)
    if (!targetEl) return

    const update = () => {
      const r = targetEl.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    update()

    const ro = new ResizeObserver(update)
    ro.observe(targetEl)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    const interval = window.setInterval(update, 250)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      window.clearInterval(interval)
    }
  }, [targetEl])

  if (!step || !rect) return null

  const holeTop = rect.top - SPOTLIGHT_PAD
  const holeLeft = rect.left - SPOTLIGHT_PAD
  const holeRight = rect.left + rect.width + SPOTLIGHT_PAD
  const holeBottom = rect.top + rect.height + SPOTLIGHT_PAD
  const holeWidth = holeRight - holeLeft
  const holeHeight = holeBottom - holeTop

  const bg = 'rgba(0, 0, 0, 0.55)'
  const backdrop: CSSProperties = {
    position: 'fixed',
    background: bg,
    zIndex: Z_BACKDROP,
    pointerEvents: 'auto',
  }
  const topStyle: CSSProperties = {
    ...backdrop,
    top: 0,
    left: 0,
    right: 0,
    height: Math.max(0, holeTop),
  }
  const bottomStyle: CSSProperties = {
    ...backdrop,
    top: Math.max(0, holeBottom),
    left: 0,
    right: 0,
    bottom: 0,
  }
  const leftStyle: CSSProperties = {
    ...backdrop,
    top: Math.max(0, holeTop),
    left: 0,
    width: Math.max(0, holeLeft),
    height: Math.max(0, holeHeight),
  }
  const rightStyle: CSSProperties = {
    ...backdrop,
    top: Math.max(0, holeTop),
    left: Math.max(0, holeRight),
    right: 0,
    height: Math.max(0, holeHeight),
  }

  const borderStyle: CSSProperties = {
    position: 'fixed',
    top: holeTop,
    left: holeLeft,
    width: holeWidth,
    height: holeHeight,
    borderRadius: 8,
    boxShadow: '0 0 0 2px #64b5f6, 0 0 24px 6px rgba(100, 181, 246, 0.55)',
    pointerEvents: 'none',
    zIndex: Z_BORDER,
  }

  const popoverStyle = computePopoverStyle(
    { top: holeTop, left: holeLeft, width: holeWidth, height: holeHeight },
    step.placement,
  )

  const blockEvent = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const onNext = (e: MouseEvent) => {
    e.stopPropagation()
    service.advance()
  }
  const onSkip = (e: MouseEvent) => {
    e.stopPropagation()
    service.skip()
  }

  return createPortal(
    <>
      <div style={topStyle} onClick={blockEvent} onMouseDown={blockEvent} />
      <div style={bottomStyle} onClick={blockEvent} onMouseDown={blockEvent} />
      <div style={leftStyle} onClick={blockEvent} onMouseDown={blockEvent} />
      <div style={rightStyle} onClick={blockEvent} onMouseDown={blockEvent} />
      <div style={borderStyle} />
      {step.blockTarget && (
        <div
          style={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            zIndex: Z_BLOCK,
          }}
          onClick={blockEvent}
          onMouseDown={blockEvent}
        />
      )}
      <Paper elevation={8} style={popoverStyle} sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {step.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
          dangerouslySetInnerHTML={{ __html: step.body }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {stepPos && (
            <Button size="small" variant="outlined" sx={{ pointerEvents: 'none' }} tabIndex={-1}>
              {stepPos.index + 1} / {stepPos.total}
            </Button>
          )}
          <Button size="small" onClick={onNext}>
            {stepPos && stepPos.index === stepPos.total - 1 ? 'Finish' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </>,
    document.body,
  )
}

function computePopoverStyle(hole: Rect, placement: TutorialPlacement): CSSProperties {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const base: CSSProperties = {
    position: 'fixed',
    width: POP_WIDTH,
    zIndex: Z_POP,
    pointerEvents: 'auto',
  }

  let top = 0
  let left = 0

  switch (placement) {
    case 'right':
      top = hole.top
      left = hole.left + hole.width + POP_OFFSET
      break
    case 'left':
      top = hole.top
      left = hole.left - POP_WIDTH - POP_OFFSET
      break
    case 'top':
      top = hole.top - POP_OFFSET - 120
      left = hole.left
      break
    case 'bottom':
    default:
      top = hole.top + hole.height + POP_OFFSET
      left = hole.left
      break
  }

  left = Math.max(8, Math.min(vw - POP_WIDTH - 8, left))
  top = Math.max(8, Math.min(vh - 160, top))

  return { ...base, top, left }
}
